/**
 * Cliente del backend real de DEENEX Activa.
 * Usa fetch directo (bypassa el mock-adapter) contra el backend en :3005.
 * Auth: con sesión de persona (el login por mail de Accesos), el token sale del canje
 * (POST /auth/activa-token); con la clave del equipo, de operador-login; sin ninguna de las dos,
 * dev-login automático (solo entorno de desarrollo).
 */

import { apiClient, getAuthToken, rolDelToken } from "../../services/api/client";
import { avisarSesionVencida } from "../../services/sesion-events";
import { TEXTO_ROL_SIN_RESPUESTA, avisarCambioRol, registrarFuenteRol } from "./rolActiva";
import { olvidarDatosDeClientes } from "./utils/borradorSinDatos";
import { textoError } from "../wa-oficial/logic/respuesta";

const API = (import.meta as any).env?.VITE_ACTIVA_API || "http://localhost:3005/api/v1";
const MARCA_DEMO = (import.meta as any).env?.VITE_DEMO_BRAND || "hatsu-demo";

/**
 * SOBRE QUÉ COMERCIO se está trabajando. Era una constante (`"hatsu-demo"`), y mientras lo fuera
 * el producto no podía atender a dos clientes: el segundo piloto escribía encima del primero.
 *
 * Ahora vive en localStorage y la elige el operador (modo operador, ver `operadorLogin`). Sin
 * marca elegida se cae a la de demo, así que el acceso directo sigue funcionando igual que antes.
 *
 * OJO: esto es sólo lo que el front PIDE. Quién manda es el token: el backend deriva el tenant del
 * `sub` verificado y descarta el brandId del body, así que cambiar esta variable a mano no da
 * acceso a nada — hay que tener un token emitido para esa marca.
 */
const MARCA_KEY = "deenex-activa:marca";
const NOMBRE_KEY = "deenex-activa:marca-nombre";
const TOKEN_KEY = "deenex-activa:token";
/** La clave del equipo que guarda SelectorMarca (`CLAVE_KEY` allá). Se lee acá para renovar el token. */
const CLAVE_OPERADOR_KEY = "deenex-activa:clave-operador";
/** El email de quien opera (SelectorMarca). No es un secreto: viaja para la auditoría del backend. */
const OPERADOR_KEY = "deenex-activa:operador";
/**
 * Cómo entró la pestaña al comercio, y por lo tanto cómo se renueva su token: "persona" (el canje de la
 * sesión del login por mail) o "clave" (la clave del equipo). Un TOKEN_KEY de antes, sin esta marca,
 * es de la clave.
 */
const MODO_KEY = "deenex-activa:modo";
/**
 * Qué es la persona que entró por mail, según el canje: «equipo» (Deenex: entra a todos los comercios y
 * hace todo) o «comercio» (alguien de un cliente: ve lo suyo, contesta conversaciones y maneja bajas).
 * Lo devuelve el backend en cada canje; la pantalla lo usa para no mostrar lo que el backend igual le
 * negaría (403 SOLO_EQUIPO). Nunca se deduce del email del token.
 */
const ROL_KEY = "deenex-activa:rol";
/** El rol del token de persona (backend: modules/accesos/roles.ts). No entra a /activa: se canjea. */
const ROL_PERSONA = "PERSONA";

/** El texto para un 403 SOLO_EQUIPO que llegue sin el suyo (el backend siempre lo manda). */
export const MENSAJE_SOLO_EQUIPO = "Esto lo hace el equipo de Deenex: pedíselo a tu contacto.";

function marcaActiva(): string {
  try { return localStorage.getItem(MARCA_KEY) || MARCA_DEMO; } catch { return MARCA_DEMO; }
}

function leerStorage(k: string): string {
  try { return localStorage.getItem(k) || ""; } catch { return ""; }
}

/**
 * La marca para la que se emitió un token (su `sub`), o "" si no se puede leer. No valida nada —la
 * firma la verifica el backend—: sólo evita que esta pestaña use un token de otro comercio.
 */
function marcaDelToken(t: string): string {
  try {
    const payload = t.split(".")[1] || "";
    return String(JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")))?.sub || "");
  } catch { return ""; }
}

/**
 * EL COMERCIO DE ESTA PESTAÑA. localStorage es uno solo para todas: si en otra se entra a otro
 * comercio, MARCA_KEY, NOMBRE_KEY y TOKEN_KEY pasan a ser de ése, pero esta pestaña sigue mostrando
 * el suyo (lo leyó al cargar). Por eso el comercio y el modo operador se leen UNA vez y sólo los
 * cambian `operadorLogin` y `salirOperador` (son `let`: al cambiar de comercio todas las llamadas
 * toman el nuevo). El storage no se vuelve a mirar para decidir sobre qué comercio se trabaja; el
 * nombre, ver `nombreActual`.
 */
let brandId = marcaActiva();
let nombre = leerStorage(NOMBRE_KEY);
let token = leerStorage(TOKEN_KEY);
/** Modo operador de ESTA pestaña: entró con un token de operador, no el acceso directo a la demo. */
let modoOperador = !!token;
/** Con qué se renueva el token de ESTA pestaña (ver MODO_KEY). Vacío fuera del modo operador. */
let modo: "persona" | "clave" | "" = modoOperador ? (leerStorage(MODO_KEY) === "persona" ? "persona" : "clave") : "";
/** ¿ESTA pestaña eligió comercio? Sin elegir, el canje no pide ninguno y el backend da el inicial (`ACTIVA_COMERCIO_INICIAL`; sin ella, la demo). */
let comercioElegido = !!leerStorage(MARCA_KEY);
type RolActiva = "equipo" | "comercio";
const leerRol = (): RolActiva | "" => {
  const r = leerStorage(ROL_KEY);
  return r === "equipo" || r === "comercio" ? r : "";
};
/** El rol que dio el último canje de ESTA pestaña. Vacío fuera del modo persona, o si no se sabe todavía. */
let rol: RolActiva | "" = modo === "persona" ? leerRol() : "";
// Un token de persona guardado sin rol (de antes de que el canje lo devolviera) no sale: se vuelve a
// canjear en el primer pedido y así se sabe qué es la persona.
if (modo === "persona" && !rol) token = "";

/** Guarda el rol del canje y avisa a la pantalla (rolActiva.ts → useEsEquipoDeenex). */
function fijarRol(nuevo: RolActiva | ""): void {
  try {
    if (nuevo) localStorage.setItem(ROL_KEY, nuevo);
    else localStorage.removeItem(ROL_KEY);
  } catch { /* sin storage: vale para esta pestaña */ }
  rol = nuevo;
  avisarCambioRol();
}
/**
 * Con la sesión de persona, el rol del canje («pendiente» hasta que llega). Con la clave del equipo, o
 * sin ninguna de las dos (el acceso directo a la demo, dev-login), «equipo»: es como fue siempre.
 */
function rolActual(): RolActiva | "pendiente" {
  if (modo === "persona" || (!modoOperador && haySesionPersona())) return rol || "pendiente";
  return "equipo";
}
/**
 * Si el rol está pendiente, canjea ya (el mismo canje que haría el primer pedido). Si después sigue pendiente, el
 * canje no anduvo y lanza qué pasó (la red o un 5xx, otra pestaña que cambió de comercio, ningún comercio asignado):
 * la página que espera el rol lo dice con «Reintentar» (rolActiva.ts → `EsperandoRol`). Antes se tragaba el error y
 * esa página quedaba en «Cargando…» para siempre.
 */
async function asegurarRol(): Promise<void> {
  if (rolActual() !== "pendiente") return;
  let fallo: unknown = null;
  try { await ensureAuth(); } catch (e) { fallo = e; }
  if (rolActual() !== "pendiente") return;
  if (comercioCambiadoEnOtraPestana()) throw errorOtraPestana();
  if (errorCanje) throw errorCanje;
  if (fallo) throw fallo;
  throw Object.assign(new Error(TEXTO_ROL_SIN_RESPUESTA), {
    status: 0,
    body: { success: false, error: TEXTO_ROL_SIN_RESPUESTA, code: "ROL_SIN_RESPUESTA" },
  });
}
registrarFuenteRol({ rolActual, asegurarRol });
/** Lo que respondió el canje cuando no hay token que dar (p. ej. un cliente sin comercios asignados). */
let errorCanje: Error | null = null;
// Un TOKEN_KEY de otro comercio (se leyó a mitad de un cambio en otra pestaña) no sale en el primer
// pedido: operaría ese comercio con la pantalla de éste. Se descarta y se renueva para `brandId`.
if (token && marcaDelToken(token) && marcaDelToken(token) !== brandId) token = "";

/** MARCA_KEY tal como la encontró o la dejó esta pestaña. Si ahora dice otra cosa, la cambió otra. */
let marcaVista = leerStorage(MARCA_KEY);

/**
 * ¿Otra pestaña entró a otro comercio (o salió del modo operador) después de que ésta cargó? Si
 * pasó, esta pestaña no pide token de ningún tipo: el 401 le llega a la pantalla con este aviso.
 * Se compara contra `marcaVista` y no contra `brandId` para que un storage que no escribe (cuota
 * llena, bloqueado) no se lea como un cambio de otra pestaña.
 */
function comercioCambiadoEnOtraPestana(): boolean {
  return leerStorage(MARCA_KEY) !== marcaVista;
}
const AVISO_OTRA_PESTANA = "Cambiaste de marca en otra pestaña. Recargá esta página para seguir trabajando sin mezclar las dos marcas.";
/** Mismo formato que un error de `req()`: wa-oficial lee `body.error` (leerError) y Activa el message. */
function errorOtraPestana(): Error {
  return Object.assign(new Error(AVISO_OTRA_PESTANA), {
    status: 401,
    body: { success: false, error: AVISO_OTRA_PESTANA, code: "COMERCIO_CAMBIADO_EN_OTRA_PESTANA" },
  });
}

/**
 * El comercio del que esta pestaña ya trajo algo (un pedido que no volvió 401): desde ahí, lo que muestra la pantalla
 * es de ese comercio. Vacío mientras no se trajo nada (la pestaña recién abierta).
 */
let comercioOperado = "";

/**
 * El comercio que esta pestaña perdió a la fuerza mientras lo mostraba: un dueño se lo sacó a la persona, el canje de
 * ese comercio dio 403 COMERCIO_NO_PERMITIDO y el canje sin comercio dio otro (el primero de los suyos). La pantalla
 * sigue siendo la del viejo: Víctor registraba una baja en hatsu-palermo, el 401 ACCESO_CAMBIADO renovaba en
 * hatsu-belgrano y `req()` reintentaba la baja ahí, con un 201 sobre la pantalla de palermo. Desde que pasa, ningún
 * pedido de esta pestaña sale (ni escrituras ni lecturas): todos dicen que el acceso cambió y que hay que recargar,
 * hasta que se recargue o se elija un comercio a mano. Con la pestaña recién abierta (todavía no trajo nada del
 * viejo) el cambio no corta nada: no hay pantalla de otro comercio.
 */
let cambioForzado: { antes: string; ahora: string } | null = null;

/** Mismo formato que un error de `req()` (como `errorOtraPestana`): wa-oficial lee `body.error` y Activa el message. */
function errorComercioCambiado(c: { antes: string; ahora: string }): Error {
  const texto = `Tu acceso cambió: ya no tenés «${c.antes}». Recargá la página para seguir en «${c.ahora}».`;
  return Object.assign(new Error(texto), { status: 409, body: { success: false, error: texto, code: "COMERCIO_CAMBIADO" } });
}

/** Corta el pedido si esta pestaña cambió de comercio a la fuerza (ver `cambioForzado`). */
function cortarSiCambioElComercio(): void {
  if (cambioForzado) throw errorComercioCambiado(cambioForzado);
}

/**
 * Cómo se nombra un comercio en el aviso: el nombre guardado si distingue a los dos (el canje devuelve el de la
 * MARCA, «HATSU», igual para palermo y belgrano), si no, su identificador.
 */
function etiquetasDelCambio(antes: { id: string; nombre: string }, ahora: { id: string; nombre: string }): { antes: string; ahora: string } {
  const distinguen = !!antes.nombre && !!ahora.nombre && antes.nombre !== ahora.nombre;
  return distinguen ? { antes: antes.nombre, ahora: ahora.nombre } : { antes: antes.id, ahora: ahora.id };
}

/**
 * La renovación en vuelo, si hay una: si vencen diez pedidos juntos, se pide un token, no diez. Vale para
 * los tres caminos (canje de persona, clave del equipo y dev-login): los diez 401 llegan casi juntos y,
 * sin esto, cada uno salía a pedir su propio token.
 */
let renovacionEnVuelo: Promise<void> | null = null;

/**
 * Renueva el token de OPERADOR sin pasar por dev-login.
 *
 * El token de operador vence a los 15 min (`JWT_ACCESS_EXPIRES_IN`, default de `token.service.ts`). Antes, al vencer, `req()` lo
 * tiraba y `ensureAuth` pedía `/auth/dev-login`: en development el token nuevo era de
 * `<marca>@dev.local` (la auditoría perdía al operador) y con `ENABLE_DEV_LOGIN=true` fuera de
 * development acuñaba la marca DEMO mientras la pantalla seguía mostrando otro comercio. En el
 * oficial eso es mandar plata y mensajes desde la marca equivocada. Ahora, en modo operador, se
 * vuelve a pedir el token a `/auth/operador-login` con la clave guardada; si no hay clave, el
 * token queda vacío y el 401 le llega a la pantalla («Se venció la sesión…»). Nunca dev-login.
 *
 * Siempre para el comercio de ESTA pestaña (`brandId`), nunca el de MARCA_KEY: ése lo pisa otra
 * pestaña al cambiar de comercio, y renovar con él dejaba a ésta operando el otro comercio sin
 * avisar (una baja de X cargada en el no-contactar de Y, una recarga acreditada a Y).
 */
async function renovarTokenOperador(): Promise<void> {
  const clave = leerStorage(CLAVE_OPERADOR_KEY);
  if (!clave) { token = ""; return; }
  const r = await fetch(`${API}/auth/operador-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ clave, brandId, operador: leerStorage(OPERADOR_KEY) || undefined }),
  });
  const j = await r.json().catch(() => ({}));
  const nuevo = r.ok ? String(j?.data?.token || "") : "";
  if (!nuevo) {
    token = "";
    // Clave rechazada (la rotaron): se olvida, igual que hace SelectorMarca al fallar (:52).
    // Si no, cada pedido volvería a probarla y el limiter del backend terminaría bloqueando al
    // operador. Un error de red o un 429 NO la borran: no dicen nada de la clave.
    if (r.status === 401) { try { localStorage.removeItem(CLAVE_OPERADOR_KEY); } catch { /* noop */ } }
    return;
  }
  // Mientras volvía la respuesta otra pestaña pudo entrar a otro comercio. Escribir este token en
  // TOKEN_KEY dejaría el storage con la marca de ella y el token de ésta: la próxima pestaña que
  // cargue (la otra misma, que se recarga al cambiar) leería la marca Y con el token de X. Ésta
  // tiene que recargar.
  if (comercioCambiadoEnOtraPestana()) { token = ""; return; }
  token = nuevo;
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* sin storage: vale para esta pestaña */ }
}

/** ¿Hay sesión de persona (el login por mail)? Se mira el token de apiClient, que pone AuthContext. */
export function haySesionPersona(): boolean {
  return rolDelToken(getAuthToken()) === ROL_PERSONA;
}

/**
 * Renueva el token de Activa con la SESIÓN DE PERSONA: el canje (POST /auth/activa-token) va por
 * apiClient, que lleva el token de persona y, si venció, lo renueva con /auth/refresh y reintenta.
 *
 * Siempre para el comercio de ESTA pestaña, igual que `renovarTokenOperador`. Sin comercio elegido no
 * pide ninguno: el backend da el inicial (al equipo) o el primero de los suyos (a un cliente), y la pestaña lo adopta
 * y lo recuerda.
 *
 * Si el canje dice que la sesión se terminó (401: murió y no se pudo renovar; 403 SIN_ACCESO: le
 * quitaron el acceso; 404: el backend no tiene el canje, así que no hay con qué seguir), el token queda
 * vacío y se avisa `deenex:sesion-vencida`: AuthContext lleva a /login. Nunca dev-login ni la clave.
 */
/**
 * Olvida el comercio de esta pestaña: el próximo canje no pide ninguno y el backend da el inicial (al
 * equipo) o el primero de los suyos (a una persona de un cliente).
 */
function olvidarComercioElegido(): void {
  brandId = MARCA_DEMO; nombre = ""; comercioElegido = false;
  try { localStorage.removeItem(MARCA_KEY); localStorage.removeItem(NOMBRE_KEY); localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
  marcaVista = leerStorage(MARCA_KEY);
}

async function renovarTokenPersona(): Promise<void> {
  let d: { token?: unknown; brandId?: unknown; marca?: unknown; rol?: unknown } | undefined;
  errorCanje = null;
  try {
    const r = await apiClient.post("/auth/activa-token", comercioElegido ? { brandId } : {});
    d = r?.data?.data;
  } catch (err: any) {
    token = "";
    const status = err?.response?.status;
    const code = err?.response?.data?.code;
    if (status === 400 && code === "BAD_INPUT") {
      // El comercio guardado no es un identificador válido (storage editado a mano o de una versión
      // vieja): se olvida y la pestaña vuelve a la demo, en vez de insistir con él en cada pedido.
      olvidarComercioElegido();
    } else if (status === 403 && code === "COMERCIO_NO_PERMITIDO") {
      if (comercioElegido) {
        // Una persona de un cliente con un comercio que ya no es suyo (se lo sacaron, o quedó guardado
        // de cuando era del equipo): se olvida y se canjea UNA vez sin comercio, que le da el primero de
        // los suyos. Sin esto, cada pedido volvía a pedir el mismo comercio y a recibir el mismo 403.
        const antes = { id: brandId, nombre };
        const loMostraba = comercioOperado === brandId;
        olvidarComercioElegido();
        await renovarTokenPersona();
        // Si la pantalla ya mostraba el viejo, desde acá ningún pedido sale: es de otro comercio (`cambioForzado`).
        if (token && loMostraba && brandId !== antes.id) cambioForzado = etiquetasDelCambio(antes, { id: brandId, nombre });
        return;
      }
      // Ni sin comercio: no tiene ninguno asignado. Se dice eso, no «HTTP 401».
      errorCanje = Object.assign(new Error(err?.response?.data?.error || "No tenés marcas asignadas: pedíselo a tu contacto de Deenex."), {
        status: 403,
        body: { success: false, error: err?.response?.data?.error || "No tenés marcas asignadas: pedíselo a tu contacto de Deenex.", code },
      });
    } else if (status === 401 || status === 404 || (status === 403 && code === "SIN_ACCESO")) {
      avisarSesionVencida();
    }
    return;
  }
  const nuevo = String(d?.token || "");
  if (!nuevo) { token = ""; return; }
  // Mientras volvía la respuesta otra pestaña pudo entrar a otro comercio: igual que en
  // `renovarTokenOperador`, este token no se escribe y esta pestaña tiene que recargar.
  if (comercioCambiadoEnOtraPestana()) { token = ""; return; }
  token = nuevo;
  // La pestaña queda en modo persona aunque el comercio lo hubiera elegido antes: si no, al recargar
  // leería un TOKEN_KEY sin modo y lo renovaría con la clave.
  modoOperador = true;
  modo = "persona";
  // Un backend sin el campo es de antes de los roles: ahí toda persona era del equipo.
  fijarRol(d?.rol === "comercio" ? "comercio" : "equipo");
  // Con la marca ya elegida y sin nombre guardado (el equipo que eligió una marca de Activa antes del 11/10: el menú
  // decía «hatsu»), se toma el del canje para esa misma marca.
  const nombreDelCanje = String(d?.marca || "").trim();
  const completarNombre = comercioElegido && !nombre && !!nombreDelCanje && String(d?.brandId || "") === brandId;
  if (!comercioElegido) {
    brandId = String(d?.brandId || brandId);
    nombre = String(d?.marca || "");
  } else if (completarNombre) {
    nombre = nombreDelCanje;
  }
  try {
    if (!comercioElegido) {
      localStorage.setItem(MARCA_KEY, brandId);
      if (nombre) localStorage.setItem(NOMBRE_KEY, nombre);
      else localStorage.removeItem(NOMBRE_KEY);
    } else if (completarNombre) {
      localStorage.setItem(NOMBRE_KEY, nombre);
    }
    localStorage.setItem(MODO_KEY, "persona");
    localStorage.setItem(TOKEN_KEY, token);
  } catch { /* sin storage: vale para esta pestaña */ }
  comercioElegido = true;
  marcaVista = leerStorage(MARCA_KEY);
}

/** Una sola renovación a la vez, sea por el canje, la clave o dev-login (ver `renovacionEnVuelo`). */
function renovarUnaVez(renovar: () => Promise<void>): Promise<void> {
  if (!renovacionEnVuelo) renovacionEnVuelo = renovar().finally(() => { renovacionEnVuelo = null; });
  return renovacionEnVuelo;
}

/**
 * El token del acceso directo (development o `ENABLE_DEV_LOGIN`). Va por `renovarUnaVez` como los otros
 * dos: una pantalla que abre con cinco pedidos a la vez, o cinco 401 juntos al vencer, pedían cinco
 * tokens, y cada uno pisaba al anterior mientras los demás pedidos salían con el que les tocara.
 */
async function renovarTokenDev(): Promise<void> {
  const r = await fetch(`${API}/auth/dev-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ brandId }),
  });
  const j = await r.json();
  token = j?.data?.token || "";
}

async function ensureAuth(): Promise<void> {
  if (token) return;
  // Otra pestaña cambió de comercio: no se pide token para ninguno (ver `renovarTokenOperador`).
  if (comercioCambiadoEnOtraPestana()) return;
  // Modo operador de esta pestaña, no «hay un TOKEN_KEY»: ése puede haberlo dejado otra pestaña, y
  // una pestaña de demo pasaría a operar el comercio real que eligió la otra. Se renueva por donde
  // entró: el canje de la persona o la clave del equipo.
  if (modoOperador) {
    await renovarUnaVez(modo === "persona" ? renovarTokenPersona : renovarTokenOperador);
    return;
  }
  // Con sesión de persona y todavía sin token de Activa: el canje. Nunca dev-login.
  if (haySesionPersona()) {
    await renovarUnaVez(renovarTokenPersona);
    return;
  }
  await renovarUnaVez(renovarTokenDev);
}

/**
 * Único camino HTTP del front de Activa y de WhatsApp oficial (`wa-oficial/api.ts` lo reusa).
 * Devuelve los 422 SIN lanzar (el llamador lee `success === false` y `code`); el resto de los
 * errores lanza un Error con `status` y `body`.
 */
export async function req(method: string, path: string, body?: unknown): Promise<any> {
  cortarSiCambioElComercio();
  await ensureAuth();
  cortarSiCambioElComercio();
  if (!token && comercioCambiadoEnOtraPestana()) throw errorOtraPestana();
  if (!token && errorCanje) throw errorCanje;
  const hit = () =>
    fetch(`${API}${path}`, {
      method,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });

  const usado = token;
  let r = await hit();

  // El token vence (15 min el de operador) y `ensureAuth` corta con `if (token) return`: sin
  // esto, una pestaña abierta un rato empieza a devolver 401 en TODO y nunca se recupera sola — el
  // operador ve "HTTP 401" en medio del wizard, con los archivos ya cargados, y no puede hacer nada
  // salvo recargar. Un 401 significa "el token murió": lo tiramos y reintentamos UNA vez.
  // Sólo se tira si es EL MISMO con el que salió el pedido: con el polling del oficial hay varios
  // pedidos en vuelo, y un 401 que llega tarde no puede borrar el token que otro ya renovó.
  // Si al renovar la pestaña perdió el comercio (`cambioForzado`), el pedido NO se repite: iría a otro.
  if (r.status === 401) {
    if (token === usado) token = "";
    await ensureAuth();
    cortarSiCambioElComercio();
    if (token) r = await hit();
    else if (comercioCambiadoEnOtraPestana()) throw errorOtraPestana();
  }
  if (r.status !== 401 && token) comercioOperado = brandId;

  const j = await r.json().catch(() => ({}));
  // Algo del equipo pedido por una persona de un cliente: la pantalla lo esconde, pero si igual llega
  // (un link viejo, otra pestaña), se dice qué pasa y no el texto genérico del 403.
  if (r.status === 403 && j?.code === "SOLO_EQUIPO" && !j.error) j.error = MENSAJE_SOLO_EQUIPO;
  if (!r.ok && r.status !== 422) throw Object.assign(new Error(j?.error || textoError({ status: r.status })), { status: r.status, body: j });
  return { status: r.status, ...j };
}

/**
 * Baja un archivo binario del backend y dispara la descarga en el navegador.
 *
 * `req()` no sirve acá: termina en `.json()` y un .xlsx se rompería. Se repite el retry de 401
 * a propósito — el token vence a la hora, y sin eso una pestaña abierta un rato larga un error
 * en la cara del operador justo cuando aprieta Descargar.
 */
export async function descargarBinario(path: string, nombreSugerido: string): Promise<void> {
  cortarSiCambioElComercio();
  await ensureAuth();
  cortarSiCambioElComercio();
  if (!token && comercioCambiadoEnOtraPestana()) throw errorOtraPestana();
  if (!token && errorCanje) throw errorCanje;
  const hit = () => fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });

  // Como `req()`: el 401 sólo tira el token si es EL MISMO con el que salió el pedido. Un Excel o un PDF
  // tardan; su 401 llegaba después de que otro pedido ya había renovado, borraba el token nuevo y
  // disparaba otra renovación.
  const usado = token;
  let r = await hit();
  if (r.status === 401) {
    if (token === usado) token = "";
    await ensureAuth();
    cortarSiCambioElComercio();
    if (token) r = await hit();
    else if (comercioCambiadoEnOtraPestana()) throw errorOtraPestana();
  }
  if (r.status !== 401 && token) comercioOperado = brandId;
  if (!r.ok) {
    // Mismo criterio que `req()`: el backend manda el motivo real en `{ error }` (helper
    // `fail()`), pero acá la respuesta puede no ser JSON — un 502 de un proxy devuelve HTML —
    // así que el parseo no puede reventar el catch de quien llama.
    const j = await r.json().catch(() => ({}));
    throw new Error(j?.error || textoError({ status: r.status }));
  }

  // El backend manda el nombre en Content-Disposition; si no llega, se usa el sugerido.
  const cd = r.headers.get("content-disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  const nombre = m?.[1] || nombreSugerido;

  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Baja un binario del backend y lo devuelve como `Blob` (las fotos, audios y archivos que manda una
 * persona en Conversaciones, que el backend trae de Twilio con las credenciales de la cuenta).
 *
 * El mismo `ensureAuth`, el mismo header y el reintento UNO del 401 que `descargarBinario`. El 401 sólo
 * tira el token si es EL MISMO con el que salió el pedido, como `req()`: un hilo con varias fotos pide
 * varias a la vez, y un 401 que llega tarde no puede borrar el token que otro pedido ya renovó.
 *
 * Un error lanza lo mismo que `req()` (`status` y `body`): sin `body`, `leerError` perdía el texto del
 * backend y un 413 de 20 MB se leía como el genérico de 8 MB de las cargas.
 */
export async function reqBlob(path: string): Promise<Blob> {
  cortarSiCambioElComercio();
  await ensureAuth();
  cortarSiCambioElComercio();
  if (!token && comercioCambiadoEnOtraPestana()) throw errorOtraPestana();
  if (!token && errorCanje) throw errorCanje;
  const hit = () => fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });

  const usado = token;
  let r = await hit();
  if (r.status === 401) {
    if (token === usado) token = "";
    await ensureAuth();
    cortarSiCambioElComercio();
    if (token) r = await hit();
    else if (comercioCambiadoEnOtraPestana()) throw errorOtraPestana();
  }
  if (r.status !== 401 && token) comercioOperado = brandId;
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw Object.assign(new Error(j?.error || textoError({ status: r.status })), { status: r.status, body: j });
  }
  return r.blob();
}

/**
 * Manda un pedazo de archivo tal cual (octet-stream) y devuelve lo mismo que `req()`: la base de ventas sube los
 * exports de a partes (un JSON con base64 corta en 8 MB, y el export de ventas de un comercio grande pesa ~79 MB).
 *
 * El mismo `ensureAuth` y el reintento UNO del 401 que `reqBlob`, con la misma regla del token. Repetir la parte
 * después de un 401 no la duplica: el 401 sale antes de que el servidor la lea, y la parte siguiente tiene que ser
 * la que falta (si no, 409).
 */
export async function reqSubida(path: string, cuerpo: Blob, senal?: AbortSignal): Promise<any> {
  cortarSiCambioElComercio();
  await ensureAuth();
  cortarSiCambioElComercio();
  if (!token && comercioCambiadoEnOtraPestana()) throw errorOtraPestana();
  if (!token && errorCanje) throw errorCanje;
  const hit = () =>
    fetch(`${API}${path}`, {
      method: "PUT",
      headers: { "Content-Type": "application/octet-stream", Authorization: `Bearer ${token}` },
      body: cuerpo,
      signal: senal,
    });

  const usado = token;
  let r = await hit();
  if (r.status === 401) {
    if (token === usado) token = "";
    await ensureAuth();
    cortarSiCambioElComercio();
    if (token) r = await hit();
    else if (comercioCambiadoEnOtraPestana()) throw errorOtraPestana();
  }
  if (r.status !== 401 && token) comercioOperado = brandId;

  const j = await r.json().catch(() => ({}));
  if (r.status === 403 && j?.code === "SOLO_EQUIPO" && !j.error) j.error = MENSAJE_SOLO_EQUIPO;
  if (!r.ok && r.status !== 422) throw Object.assign(new Error(j?.error || textoError({ status: r.status })), { status: r.status, body: j });
  return { status: r.status, ...j };
}

type FileArg = { content: string; encoding?: "csv" | "base64" };

/** Un comercio (inquilino): tiene su propia base de clientes, campañas y WhatsApp. */
export type Comercio = { brandId: string; marca: string; campanas: number; ultima: string | null };

/** Una marca del padrón real de la plataforma, con sus locales. El brandId es su _id. */
export type MarcaReal = {
  marca: string; brandId: string; campanas: number; enActiva: boolean;
  locales: { id: string; nombre: string }[];
};

/** Los comercios del selector, agrupados por marca: lo que arma `activaService.listarMarcas`. */
export type ListaMarcas = {
  fuenteCatalogo: "plataforma" | "local";
  catalogo: MarcaReal[];
  marcas: { marca: string; campanas: number; comercios: Comercio[] }[];
  sueltos: Comercio[];
  total: number;
};

/**
 * La lista de comercios tal como llega del backend, con los defaults puestos. La devuelven los dos
 * caminos del selector: con la clave (POST /activa/operador/marcas) y con la sesión de persona
 * (GET /accesos/marcas).
 */
export function listaDeMarcas(data: unknown): ListaMarcas {
  const d = (data && typeof data === "object" ? data : {}) as Record<string, any>;
  return {
    /** de dónde salió el padrón: "plataforma" = las marcas REALES de Palta */
    fuenteCatalogo: (d.fuenteCatalogo || "local") as "plataforma" | "local",
    /** las marcas reales, cada una con sus locales */
    catalogo: (d.catalogo || []) as MarcaReal[],
    marcas: (d.marcas || []) as { marca: string; campanas: number; comercios: Comercio[] }[],
    sueltos: (d.sueltos || []) as Comercio[],
    total: (d.total || 0) as number,
  };
}

/**
 * Olvida el comercio y el token de Activa de ESTA sesión (lo llama AuthContext al entrar, al salir y
 * cuando la sesión vence): el token, el comercio, su nombre, la clave del equipo y el modo. Sin esto,
 * quien entraba después en el mismo navegador seguía con el token de Activa del anterior, y la
 * auditoría le atribuía todo al otro. También borra los datos de clientes que el asistente de
 * Reactivación dejó en el navegador (`olvidarDatosDeClientes`). El mail declarado del operador y el
 * borrador de una campaña ya analizada no son de la sesión: se quedan.
 */
export function olvidarSesionActiva(): void {
  token = ""; brandId = MARCA_DEMO; nombre = ""; modoOperador = false; modo = ""; comercioElegido = false; errorCanje = null;
  cambioForzado = null; comercioOperado = "";
  try {
    for (const k of [TOKEN_KEY, MARCA_KEY, NOMBRE_KEY, CLAVE_OPERADOR_KEY, MODO_KEY]) localStorage.removeItem(k);
    olvidarDatosDeClientes(localStorage);
  } catch { /* noop */ }
  fijarRol("");
  marcaVista = leerStorage(MARCA_KEY);
}

export const activaApi = {
  /** getter: si el operador cambia de comercio, quien lea esto ve el nuevo, no el del arranque */
  get brandId() { return brandId; },

  /** el comercio sobre el que se está trabajando ahora */
  marcaActual: () => brandId,
  /** El NOMBRE legible del comercio ("La Fábrica"), para no mostrarle un ObjectId al operador.
   *  Cae al id si no se guardó (comercios creados a mano o sesiones viejas). Si otra pestaña
   *  cambió de comercio, NOMBRE_KEY ya es el de ése: se muestra el que conocía esta pestaña, que es
   *  sobre el que sigue trabajando. */
  nombreActual: () => (comercioCambiadoEnOtraPestana() ? nombre : leerStorage(NOMBRE_KEY)) || brandId,
  /** ¿está en modo operador (eligió comercio) o es el acceso directo a la marca demo? De ESTA pestaña. */
  esOperador: () => modoOperador,

  /**
   * ¿Quien opera ESTA pestaña es del equipo de Deenex? Con la sesión de persona (el login por mail),
   * lo dice el rol que devolvió el canje: hasta que llega, no (se esconden las acciones del equipo un
   * instante en vez de mostrarlas de más). Con la clave del equipo, sí. Sin ninguna de las dos (el
   * acceso directo a la demo o dev-login), sí: es como fue siempre. Nunca sale del email del token. El
   * backend frena igual lo que no corresponde: esto sólo decide qué se muestra.
   */
  esEquipoDeenex: (): boolean => rolActual() === "equipo",

  /** El rol de esta pestaña, o «pendiente» si la sesión de persona todavía no canjeó (ver `esEquipoDeenex`). */
  rolActual: (): RolActiva | "pendiente" => rolActual(),

  /**
   * Si el rol está pendiente, canjea ya (el mismo canje que haría el primer pedido). Lo usa una página
   * que no muestra nada hasta saber el rol: sin esto esperaría un pedido que nunca hace. Si el canje no
   * anduvo, lanza qué pasó (la pantalla lo pide por rolActiva.ts, que guarda el error para «Reintentar»).
   */
  asegurarRol,

  /**
   * MODO OPERADOR: valida la clave del equipo contra el backend y pide un token para ESE comercio.
   * El token queda guardado; a partir de ahí todo el módulo trabaja sobre esa marca.
   */
  operadorLogin: async (clave: string, comercio: string, marca?: string, nombreLegible?: string, operador?: string) => {
    const r = await fetch(`${API}/auth/operador-login`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      // `brandId` = el comercio (el inquilino); `marca` = a qué marca pertenece (sólo agrupa);
      // `operador` = el email de quien entra, para la auditoría (sin él, `undefined` no viaja)
      body: JSON.stringify({ clave, brandId: comercio, marca, operador }),
    });
    const j = await r.json();
    if (!r.ok || !j?.data?.token) throw new Error(j?.error || "No se pudo entrar como operador");
    token = j.data.token;
    brandId = j.data.brandId;
    modoOperador = true;
    modo = "clave";
    // la clave es del equipo: el rol de una sesión de persona anterior no aplica
    fijarRol("");
    comercioElegido = true;
    // elegir un comercio a mano destraba la pestaña después de un cambio a la fuerza (`cambioForzado`)
    cambioForzado = null; comercioOperado = "";
    nombre = nombreLegible || "";
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(MARCA_KEY, brandId);
      localStorage.setItem(MODO_KEY, "clave");
      if (nombreLegible) localStorage.setItem(NOMBRE_KEY, nombreLegible);
      else localStorage.removeItem(NOMBRE_KEY);
    } catch { /* sin storage: vale para esta pestaña */ }
    marcaVista = leerStorage(MARCA_KEY);
    return j.data as { token: string; brandId: string; marca?: string };
  },

  /**
   * MODO PERSONA: quien entró con su mail (Accesos) pide con el canje un token para ESE comercio. Sin
   * clave: la sesión de persona ya dice quién es, y el backend vuelve a mirar la lista en cada pedido.
   * Guarda lo mismo que `operadorLogin`, más el modo: al vencer, el token se renueva con el canje.
   */
  entrarComoPersona: async (comercio: string, marca?: string, nombreLegible?: string) => {
    let d: { token?: string; brandId?: string; marca?: string; rol?: string } | undefined;
    try {
      // `marca` = a qué marca pertenece el comercio (sólo agrupa); sin ella no viaja
      const r = await apiClient.post("/auth/activa-token", marca ? { brandId: comercio, marca } : { brandId: comercio });
      d = r?.data?.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "No se pudo entrar a esa marca");
    }
    if (!d?.token) throw new Error("No se pudo entrar a esa marca");
    token = d.token;
    brandId = d.brandId || comercio;
    modoOperador = true;
    modo = "persona";
    comercioElegido = true;
    errorCanje = null;
    // elegir un comercio a mano destraba la pestaña después de un cambio a la fuerza (`cambioForzado`)
    cambioForzado = null; comercioOperado = "";
    // Sin el nombre de la pantalla, el que devuelve el canje (el guardado de la marca): sin ninguno, el menú mostraba el
    // identificador.
    nombre = nombreLegible || String(d.marca || "").trim();
    fijarRol(d.rol === "comercio" ? "comercio" : "equipo");
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(MARCA_KEY, brandId);
      localStorage.setItem(MODO_KEY, "persona");
      if (nombre) localStorage.setItem(NOMBRE_KEY, nombre);
      else localStorage.removeItem(NOMBRE_KEY);
    } catch { /* sin storage: vale para esta pestaña */ }
    marcaVista = leerStorage(MARCA_KEY);
    return d as { token: string; brandId: string; marca?: string };
  },

  /**
   * Los comercios ya cargados, AGRUPADOS POR MARCA (una marca tiene varios locales).
   * Pide la clave: la lista es la cartera de clientes de Deenex.
   */
  listarMarcas: async (clave: string) => {
    const r = await fetch(`${API}/activa/operador/marcas`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clave }),
    });
    const j = await r.json();
    if (!r.ok) throw new Error(j?.error || "No se pudo traer la lista de marcas");
    return listaDeMarcas(j.data);
  },

  /** Vuelve al acceso directo (marca demo): olvida el token de operador. */
  salirOperador: () => {
    token = ""; brandId = MARCA_DEMO; nombre = ""; modoOperador = false; modo = ""; comercioElegido = false; errorCanje = null;
    cambioForzado = null; comercioOperado = "";
    try {
      localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(MARCA_KEY); localStorage.removeItem(NOMBRE_KEY);
      localStorage.removeItem(MODO_KEY);
    } catch { /* noop */ }
    fijarRol("");
    marcaVista = leerStorage(MARCA_KEY);
  },

  parse: (pedidos: FileArg, clientes: FileArg) => req("POST", "/activa/parse", { brandId, pedidos, clientes }),
  /** @param origen "ejemplo" = los archivos son el dataset de muestra: la campaña queda marcada y
   *  el backend no la deja enviar ni programar (sus contactos son personas ajenas a la marca). */
  analyze: (nombre: string, marca: string | undefined, pedidos: FileArg, clientes: FileArg, mapeoPedidos?: any, mapeoClientes?: any, origen?: "ejemplo") =>
    req("POST", "/activa/analyze", { brandId, nombre, marca, pedidos, clientes, mapeoPedidos, mapeoClientes, origen }),
  context: (id: string, context: any) => req("POST", `/activa/campaigns/${id}/context`, { brandId, context }),
  /** El nombre del comercio que firma los mensajes de la campaña. Si ya estaban armados, el backend los rearma.
   *  Un 422 (vacío, muy largo, ya salió) vuelve con success:false y el motivo. */
  cambiarMarca: (id: string, marca: string) => req("POST", `/activa/campaigns/${id}/marca`, { brandId, marca }),
  /**
   * Ensayo de voz: cómo suena un mensaje con la voz elegida, calculado por el generador REAL.
   * No toca la base ni necesita campaña — sirve mientras se está eligiendo el tono.
   */
  ensayarVoz: (marca: string, context: any, grupoId?: string) =>
    req("POST", "/activa/voz/ensayo", { brandId, marca, context, grupoId }),
  /** Config de la marca: política de consentimiento + la VOZ recordada de la última campaña. */
  settings: () => req("GET", `/activa/settings?brandId=${brandId}`),
  messages: (id: string) => req("POST", `/activa/campaigns/${id}/messages`, { brandId }),
  /** @param grupos ids de grupo a los que enviar. Omitido = todos. @param lineas ids de línea de
   *  WhatsApp DESDE las que sale. Omitido = todas las conectadas (no se puede inferir "ninguna"). */
  send: (id: string, grupos?: string[], lineas?: string[]) => req("POST", `/activa/campaigns/${id}/send`, { brandId, grupos, lineas }),
  /** Calcula cuántos mensajes saldrían, sin mandar ninguno (para confirmar con el número a la vista). */
  sendDryRun: (id: string, grupos?: string[], lineas?: string[]) => req("POST", `/activa/campaigns/${id}/send?dryRun=true`, { brandId, dryRun: true, grupos, lineas }),
  /** Programa el envío. `cuando` en ISO; el scheduler la dispara sola al llegar la hora. */
  schedule: (id: string, cuando: string, grupos?: string[], lineas?: string[]) => req("POST", `/activa/campaigns/${id}/schedule`, { brandId, cuando, grupos, lineas }),
  cancelSchedule: (id: string) => req("DELETE", `/activa/campaigns/${id}/schedule?brandId=${brandId}`),
  /** El reparto de los WhatsApp entre las líneas por QR y WhatsApp oficial (API): lo pedido, lo que hay y si se puede cambiar. */
  reparto: (id: string) => req("GET", `/activa/campaigns/${id}/reparto?brandId=${brandId}`),
  /** Reparte `qr` por las líneas y `api` por WhatsApp oficial. Un 422 (no alcanza, ya salió una parte) vuelve sin
   *  lanzar, con el motivo y `disponibles`. */
  repartir: (id: string, qr: number, api: number) => req("PUT", `/activa/campaigns/${id}/reparto`, { brandId, qr, api }),
  /** Saca el reparto: las dos vías vuelven a escribirles a todos. */
  quitarReparto: (id: string) => req("DELETE", `/activa/campaigns/${id}/reparto?brandId=${brandId}`),
  /** El texto exacto que va a recibir cada grupo, renderizado con un nombre real de la base. */
  preview: (id: string) => req("GET", `/activa/campaigns/${id}/preview?brandId=${brandId}`),
  /** Reemplaza a mano el mensaje de un grupo. Texto vacío = volver al automático. */
  editarMensaje: (id: string, grupoId: string, texto: string) =>
    req("POST", `/activa/campaigns/${id}/mensaje`, { brandId, grupoId, texto }),
  /** Manda el mensaje real de un grupo a UN número, para verlo en el teléfono antes del envío. */
  testSendCampana: (id: string, grupoId: string, to: string, nombre?: string) =>
    req("POST", `/activa/campaigns/${id}/test-send`, { brandId, grupoId, to, nombre }),
  measure: (id: string) => req("POST", `/activa/campaigns/${id}/measure`, { brandId }),
  /** Carga la base de SEGUIMIENTO (los 2 CSV actualizados/acumulativos del período posterior) y
   *  cierra la medición: cuántos volvieron y cuánta plata se recuperó. Es lo que convierte el piloto
   *  en caso de éxito, sin esperar el cambio de mes calendario. */
  cargarSeguimiento: (id: string, pedidos: FileArg, clientes: FileArg) =>
    req("POST", `/activa/campaigns/${id}/seguimiento`, { brandId, pedidos, clientes }),
  results: (id: string) => req("GET", `/activa/campaigns/${id}/results?brandId=${brandId}`),
  responses: (id: string) => req("GET", `/activa/campaigns/${id}/responses?brandId=${brandId}`),
  /** Bitácora por persona. El backend enmascara la PII salvo reveal=true. */
  events: (id: string, opts: Record<string, string | number | boolean | undefined> = {}) => {
    const qs = Object.entries({ brandId, ...opts })
      .filter(([, v]) => v !== undefined && v !== "")
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join("&");
    return req("GET", `/activa/campaigns/${id}/events?${qs}`);
  },
  campaigns: () => req("GET", `/activa/campaigns?brandId=${brandId}`),
  campaign: (id: string) => req("GET", `/activa/campaigns/${id}?brandId=${brandId}`),

  /**
   * El Excel de la segmentación: una hoja por grupo, más Resumen y casos de borde.
   *
   * Baja con la identidad COMPLETA, no enmascarada. El archivo existe para que el dueño del
   * comercio mire su propia base y decida si los grupos están bien armados — y para eso tiene
   * que reconocer a la gente. Con `c***@hotmail.com` y `+54****8950` no se reconoce a nadie:
   * el enmascarado no protegía a nadie acá (son SUS clientes, los tiene en su propio CSV),
   * sólo volvía inútil la columna.
   *
   * El resguardo sigue en pie donde importa: el archivo se descarga como
   * `segmentacion_PII_<id>.xlsx` —se ve en la carpeta de Descargas que lleva datos sin
   * enmascarar— y el backend deja el registro de auditoría de quién lo pidió. Los niveles
   * `enmascarado` y `anonimo` siguen disponibles por URL para cuando el archivo sale del
   * comercio, que es el caso para el que se habían pensado.
   */
  descargarSegmentacionXLSX: async (
    campaignId: string,
    pii: "anonimo" | "enmascarado" | "completo" = "completo",
  ): Promise<void> => {
    await descargarBinario(
      `/activa/campaigns/${campaignId}/segmentacion?formato=xlsx&pii=${pii}`,
      `segmentacion_PII_${campaignId}.xlsx`,
    );
  },

  /**
   * El reporte comercial como PDF de verdad (no `window.print()`, que abre el diálogo de
   * impresión del navegador). El backend renderiza la vista pública `/r/:token` con Chromium
   * headless y devuelve el archivo — acá sólo se baja con el mismo helper que ya prueba el
   * Excel de segmentación (retry de 401 incluido).
   */
  descargarReportePDF: async (campaignId: string): Promise<void> => {
    await descargarBinario(
      `/activa/campaigns/${campaignId}/reporte.pdf`,
      `reporte_${campaignId}.pdf`,
    );
  },

  // Link público del reporte comercial
  share: (id: string) => req("POST", `/activa/campaigns/${id}/share`, { brandId }),
  /** Deja de compartir el link público: el link viejo pasa a dar 404. Compartir de nuevo crea otro. */
  unshare: (id: string) => req("DELETE", `/activa/campaigns/${id}/share?brandId=${brandId}`),
  /** Reporte público por token — SIN auth (página compartible). */
  publicReport: async (tokenPub: string) => {
    const r = await fetch(`${API}/activa/public/report/${encodeURIComponent(tokenPub)}`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(j?.error || textoError({ status: r.status })), { status: r.status });
    return j.data;
  },

  /**
   * Baja desde el link del mail — SIN auth: quien la usa es el cliente final, no el operador.
   * `publicBajaInfo` sólo dice de qué marca es (no tiene efecto: los previsualizadores de links
   * abren las URL solos); `publicBaja` es la que da la baja, con el botón de la página.
   */
  publicBajaInfo: async (tokenBaja: string): Promise<{ marca: string }> => {
    const r = await fetch(`${API}/activa/public/baja/${encodeURIComponent(tokenBaja)}`);
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(j?.error || textoError({ status: r.status })), { status: r.status });
    return j.data;
  },
  publicBaja: async (tokenBaja: string): Promise<{ baja: true; marca: string }> => {
    const r = await fetch(`${API}/activa/public/baja/${encodeURIComponent(tokenBaja)}`, { method: "POST" });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error(j?.error || textoError({ status: r.status })), { status: r.status });
    return j.data;
  },

  // No-contactar (opt-out / bajas)
  dncList: () => req("GET", `/activa/do-not-contact?brandId=${brandId}`),
  dncAdd: (contacto: string, canal: "WhatsApp" | "Email", motivo?: string) =>
    req("POST", "/activa/do-not-contact", { brandId, contacto, canal, motivo }),
  /** Levanta una baja (revierte un falso positivo): la persona vuelve a ser contactable.
   *  El backend deja rastro (levantadaAt/Por/motivo). `dncList` ya filtra activo:true → tras
   *  levantar, la fila desaparece sola con un refresco. */
  dncLevantar: (id: string, motivo: string) =>
    req("POST", `/activa/do-not-contact/${id}/levantar`, { brandId, motivo }),

  // Circuito de conexión de WhatsApp (QR)
  lines: () => req("GET", `/activa/lines?brandId=${brandId}`),
  createLine: (nombre: string) => req("POST", "/activa/lines", { brandId, nombre }),
  getLine: (id: string) => req("GET", `/activa/lines/${id}?brandId=${brandId}`),
  connectLine: (id: string) => req("POST", `/activa/lines/${id}/connect`, { brandId }),
  pairLine: (id: string, phone: string) => req("POST", `/activa/lines/${id}/pair`, { brandId, phone }),
  disconnectLine: (id: string) => req("POST", `/activa/lines/${id}/disconnect`, { brandId }),
  /** borra la línea y su sesión (deshacer un intento de vinculación que no llegó a nada) */
  deleteLine: (id: string) => req("DELETE", `/activa/lines/${id}?brandId=${brandId}`),
  testSend: (id: string, to: string, text: string) => req("POST", `/activa/lines/${id}/test-send`, { brandId, to, text }),
  /** respuestas que llegaron a la línea sin campaña atribuible; el backend ya enmascara el número */
  lineResponses: (id: string) => req("GET", `/activa/lines/${id}/responses?brandId=${brandId}`),
  /** estadísticas de entrega/lectura de los mensajes de prueba (enviado → entregado ✓✓ → leído ✓✓ azul) */
  testStats: () => req("GET", `/activa/lines/test-stats?brandId=${brandId}`),

  // Cuentas de email de la marca (el otro canal). La contraseña SMTP se manda al crear/editar
  // pero NUNCA vuelve: el backend la cifra y el schema la tiene en select:false.
  emailAccounts: () => req("GET", `/activa/email-accounts?brandId=${brandId}`),
  createEmailAccount: (data: Record<string, unknown>) => req("POST", "/activa/email-accounts", { brandId, ...data }),
  updateEmailAccount: (id: string, data: Record<string, unknown>) => req("PATCH", `/activa/email-accounts/${id}`, { brandId, ...data }),
  deleteEmailAccount: (id: string) => req("DELETE", `/activa/email-accounts/${id}?brandId=${brandId}`),
  /** prueba la conexión SMTP de verdad (equivalente a escanear el QR de una línea) */
  verifyEmailAccount: (id: string) => req("POST", `/activa/email-accounts/${id}/verify`, { brandId }),
  testSendEmailAccount: (id: string, to: string) => req("POST", `/activa/email-accounts/${id}/test-send`, { brandId, to }),
};
